import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@common/components';
import { faCopy, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import classes from './CopyId.module.scss';

const FEEDBACK_MS = 1500;

/** navigator.clipboard needs a secure context; the textarea trick covers plain-http deployments. */
async function writeClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(area);
  if (!ok) throw new Error('copy failed');
}

/**
 * A suitcase id ("BR_S4049@C6F4-6EFA-E1C9-3EB7") that can be copied without fighting the row it
 * lives in: one click selects the school or the licence part, the button copies the whole id.
 * Clicks here never reach the row, so they do not open or close it.
 */
export default function CopyId({ value }) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });
  const [state, setState] = useState('idle'); // idle | copied | failed
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(
    async (event) => {
      event.stopPropagation();
      try {
        await writeClipboard(value);
        setState('copied');
      } catch (e) {
        setState('failed');
      }
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setState('idle'), FEEDBACK_MS);
    },
    [value],
  );

  if (!value) return null;

  const [school, licence] = value.split('@');
  const stop = (event) => event.stopPropagation();
  const icon = { idle: faCopy, copied: faCheck, failed: faTimes }[state];

  return (
    <span
      className={classes.id}
      onClick={stop}
      onKeyDown={stop}
      role="presentation"
    >
      <span className={classes.part} title={t('copyIdSelect')}>
        {school}
      </span>
      {licence !== undefined && (
        <>
          <span className={classes.sep}>@</span>
          <span className={classes.part} title={t('copyIdSelect')}>
            {licence}
          </span>
        </>
      )}
      <button
        type="button"
        className={`${classes.btn} ${
          state === 'copied' ? classes.btnDone : ''
        } ${state === 'failed' ? classes.btnFailed : ''}`}
        onClick={copy}
        title={t(state === 'idle' ? 'copyIdCopy' : `copyId_${state}`)}
        aria-label={t('copyIdCopy')}
      >
        <Icon icon={icon} />
      </button>
    </span>
  );
}

CopyId.propTypes = {
  value: PropTypes.string,
};

CopyId.defaultProps = {
  value: '',
};
